import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, Bell, AlertTriangle, ChevronRight, ShieldCheck, Phone, Mail, Crown, Users } from 'lucide-react';
import { Branch, BranchStaff, useBranches, roleMeta, roleColorMap, avatarColorMap, permissionMeta, defaultPermissionsForRole, defaultNotificationsForRole } from './BranchesContext';
import { StaffEditorModal } from './StaffEditorModal';
export function BranchStaffSection({
  branch
}: {
  branch: Branch;
}) {
  const {
    addStaff,
    updateStaff,
    removeStaff
  } = useBranches();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const newStaffTemplate: BranchStaff = useMemo(() => ({
    id: `__new__`,
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    role: 'staff',
    active: true,
    avatarColor: 'orange',
    permissions: defaultPermissionsForRole('staff'),
    notifications: defaultNotificationsForRole('staff'),
    addedAt: Date.now()
  }), [isAdding]);
  const editingStaff = editingId !== null ? branch.staff.find(s => s.id === editingId) : null;
  // Routing summary chips at the section level
  const newOrderRecipients = branch.staff.filter(s => s.active && s.notifications.newOrderSMS);
  const lowStockRecipients = branch.staff.filter(s => s.active && s.notifications.lowStockSMS);
  return <div data-id="element-762">
      {/* Routing summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4" data-id="element-763">
        <RoutingSummary icon={<Bell size={13} data-id="element-765" />} label="New order SMS" recipients={newOrderRecipients} tint="orange" data-id="element-764" />
        <RoutingSummary icon={<AlertTriangle size={13} data-id="element-767" />} label="Low-stock SMS" recipients={lowStockRecipients} tint="rose" data-id="element-766" />
      </div>

      {/* Staff list */}
      <div className="space-y-2" data-id="element-768">
        {branch.staff.length === 0 && <div className="text-center py-8 px-4 rounded-xl border border-dashed border-gray-300 bg-gray-50/50" data-id="element-769">
            <Users size={20} className="mx-auto text-gray-400 mb-2" strokeWidth={1.5} data-id="element-770" />
            <div className="text-sm font-semibold text-gray-700" data-id="element-771">
              No team members yet
            </div>
            <div className="text-xs text-gray-500 mt-0.5" data-id="element-772">
              Add managers and staff so they can receive alerts and access this
              branch.
            </div>
          </div>}
        {branch.staff.map(s => <StaffRow key={s.id} staff={s} onClick={() => setEditingId(s.id)} onToggleNotif={(key, value) => updateStaff(branch.id, s.id, {
        notifications: {
          ...s.notifications,
          [key]: value
        }
      })} data-id="element-773" />)}
      </div>

      {/* Add button */}
      <button onClick={() => setIsAdding(true)} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-gray-300 text-sm font-semibold text-gray-600 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50/40 transition-colors" data-id="element-774">
        <UserPlus size={15} data-id="element-775" />
        Add team member
      </button>

      {/* Edit modal */}
      <AnimatePresence data-id="element-776">
        {editingStaff && <StaffEditorModal staff={editingStaff} isNew={false} onClose={() => setEditingId(null)} onSave={patch => updateStaff(branch.id, editingStaff.id, patch)} onDelete={() => removeStaff(branch.id, editingStaff.id)} data-id="element-777" />}
        {isAdding && <StaffEditorModal staff={newStaffTemplate} isNew onClose={() => setIsAdding(false)} onSave={patch => {
        const {
          id: _ignore,
          ...rest
        } = patch as BranchStaff;
        addStaff(branch.id, rest);
      }} data-id="element-778" />}
      </AnimatePresence>
    </div>;
}
function StaffRow({
  staff,
  onClick,
  onToggleNotif
}: {
  staff: BranchStaff;
  onClick: () => void;
  onToggleNotif: (key: 'newOrderSMS' | 'lowStockSMS', value: boolean) => void;
}) {
  const meta = roleMeta[staff.role];
  const c = roleColorMap[meta.color];
  const initials = (staff.firstName[0] || '').toUpperCase() + (staff.lastName[0] || '').toUpperCase();
  const fullName = `${staff.firstName} ${staff.lastName}`.trim() || 'Untitled member';
  const grantedCount = Object.values(staff.permissions).filter(Boolean).length;
  const totalPerms = permissionMeta.length;
  return <div className={`group rounded-xl border bg-white p-3 transition-colors ${staff.active ? 'border-gray-200 hover:border-gray-300' : 'border-gray-200 opacity-70'}`} data-id="element-779">
      <div className="flex items-start gap-3" data-id="element-780">
        {/* Avatar */}
        <div className="relative flex-shrink-0" data-id="element-781">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${avatarColorMap[staff.avatarColor] || avatarColorMap.orange}`} data-id="element-782">
            {initials || '?'}
          </div>
          {staff.role === 'manager' && <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-amber-400 flex items-center justify-center ring-2 ring-white" data-id="element-783">
              <Crown size={9} className="text-white" data-id="element-784" />
            </div>}
        </div>

        {/* Main */}
        <div className="flex-1 min-w-0" data-id="element-785">
          <button onClick={onClick} className="w-full text-left flex items-start justify-between gap-2" data-id="element-786">
            <div className="min-w-0" data-id="element-787">
              <div className="flex items-center gap-2 flex-wrap" data-id="element-788">
                <span className="font-bold text-sm text-gray-900 truncate" data-id="element-789">
                  {fullName}
                </span>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${c.bg} ${c.text}`} data-id="element-790">
                  {meta.label}
                </span>
                {!staff.active && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-gray-200 text-gray-600" data-id="element-791">
                    Inactive
                  </span>}
              </div>
              <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-0.5 flex-wrap" data-id="element-792">
                {staff.phone && <span className="inline-flex items-center gap-1 tabular-nums" data-id="element-793">
                    <Phone size={10} data-id="element-794" />
                    {staff.phone}
                  </span>}
                {staff.email && <span className="inline-flex items-center gap-1 truncate" data-id="element-795">
                    <Mail size={10} data-id="element-796" />
                    <span className="truncate" data-id="element-797">{staff.email}</span>
                  </span>}
              </div>
              <div className="flex items-center gap-1 mt-1.5 text-[10px] text-gray-500" data-id="element-798">
                <ShieldCheck size={10} className="text-emerald-500" data-id="element-799" />
                <span className="font-semibold text-gray-600" data-id="element-800">
                  {grantedCount}
                </span>
                <span data-id="element-801">of {totalPerms} permissions</span>
              </div>
            </div>
            <ChevronRight size={15} className="text-gray-300 group-hover:text-orange-500 transition-colors flex-shrink-0 mt-1" data-id="element-802" />
          </button>

          {/* Quick notification toggles */}
          <div className="mt-2.5 flex items-center gap-1.5 flex-wrap" data-id="element-803">
            <NotifChip icon={<Bell size={10} data-id="element-805" />} label="New order" active={staff.notifications.newOrderSMS && staff.active} disabled={!staff.active} onClick={() => onToggleNotif('newOrderSMS', !staff.notifications.newOrderSMS)} tint="orange" data-id="element-804" />
            <NotifChip icon={<AlertTriangle size={10} data-id="element-807" />} label="Low stock" active={staff.notifications.lowStockSMS && staff.active} disabled={!staff.active} onClick={() => onToggleNotif('lowStockSMS', !staff.notifications.lowStockSMS)} tint="rose" data-id="element-806" />
          </div>
        </div>
      </div>
    </div>;
}
function NotifChip({
  icon,
  label,
  active,
  disabled,
  onClick,
  tint
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  tint: 'orange' | 'rose';
}) {
  const activeClasses = tint === 'orange' ? 'bg-orange-100 text-orange-700 ring-1 ring-orange-200' : 'bg-rose-100 text-rose-700 ring-1 ring-rose-200';
  return <button onClick={onClick} disabled={disabled} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold transition-colors disabled:cursor-not-allowed ${active ? activeClasses : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`} data-id="element-808">
      {icon}
      {label}
      <span className="text-[9px] opacity-75 ml-0.5" data-id="element-809">
        {active ? 'ON' : 'OFF'}
      </span>
    </button>;
}
function RoutingSummary({
  icon,
  label,
  recipients,
  tint
}: {
  icon: React.ReactNode;
  label: string;
  recipients: BranchStaff[];
  tint: 'orange' | 'rose';
}) {
  const tintBg = tint === 'orange' ? 'bg-orange-50' : 'bg-rose-50';
  const tintText = tint === 'orange' ? 'text-orange-700' : 'text-rose-700';
  const tintIconBg = tint === 'orange' ? 'bg-orange-100 text-orange-600' : 'bg-rose-100 text-rose-600';
  return <div className="rounded-xl border border-gray-200 bg-white p-3" data-id="element-810">
      <div className="flex items-center gap-2 mb-2" data-id="element-811">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${tintIconBg}`} data-id="element-812">
          {icon}
        </div>
        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide" data-id="element-813">
          {label}
        </div>
        <span className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded ${recipients.length > 0 ? `${tintBg} ${tintText}` : 'bg-gray-100 text-gray-500'}`} data-id="element-814">
          {recipients.length}
        </span>
      </div>
      {recipients.length === 0 ? <div className="text-[11px] text-gray-500 italic" data-id="element-815">
          No one is set to receive these alerts.
        </div> : <div className="flex flex-wrap items-center gap-1" data-id="element-816">
          {recipients.slice(0, 4).map(r => <span key={r.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold text-gray-700 bg-gray-100" data-id="element-817">
              <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] font-bold ${avatarColorMap[r.avatarColor] || avatarColorMap.orange}`} data-id="element-818">
                {(r.firstName[0] || '?').toUpperCase()}
              </span>
              {r.firstName}
            </span>)}
          {recipients.length > 4 && <span className="text-[10px] font-semibold text-gray-500" data-id="element-819">
              +{recipients.length - 4} more
            </span>}
        </div>}
    </div>;
}